import { computed, Directive, effect, inject, input, Signal, signal, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { IManualDeliverableStatusConfig } from '@cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-devlierable-status.interface';
import { IKpiCard } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { ICtaAnalyticsData } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { DeliverableFindingsService } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.service';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { FindingDeleteService } from '@cta-analytics-dialog/deliverable-analytics-content/finding-delete.service';
import { ManualFindingStatusStore } from '@cta-analytics-dialog/manual-finding-status.store';
import { EAnalyticsStatus, EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType, EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import {
  IBaseDeliverableAnalyticsApi,
  ITimeline,
  ITwitchStreamApi,
  IYoutubeStreamApi,
} from '@interfaces/campaign-analytics-api.interface';
import { IFinding, IKeywordHolder } from '@interfaces/deliverable-analytics.interface';
import { IFindingVisualizer } from '@interfaces/finding-visualizer.interface';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { DateUtilitiesService, DISPLAY_TIME_FORMAT } from '@utilities/date-utilities.service';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { GraphUtilitiesService } from '@utilities/graph-utilities.service';
import { ILineGraphInputData } from '../../graphs/line-graph/line-graph.interface';
import { StreamDetailsService } from './stream-details.service';

@Directive({
  providers: [DeliverableFindingsStore],
  selector: '[appStreamDetailsDeliverableBase]',
})
export abstract class StreamDetailsDeliverableBaseDirective<
  TableData extends {
    id: string;
    timestamp?: string | null;
    videoTimeLabel?: string;
    videoTimeSeconds?: number | null;
  },
  Deliverable extends IBaseDeliverableAnalyticsApi,
> {
  public readonly platform = input.required<EPlatform>();
  public readonly deliverableForm = input.required<FormGroup<TDeliverableForm>>();
  public readonly deliverableId = input.required<string | null>();
  public readonly executionAnalytics = input.required<ICtaAnalyticsData | null>();
  public readonly timelineData = input.required<ITimeline[] | null>();
  public readonly viewerGraphData = input.required<ILineGraphInputData[]>();
  public readonly manualDeliverableStatusConfig = input.required<IManualDeliverableStatusConfig>();

  public abstract readonly displayedColumns: string[];
  public abstract readonly dataSource: MatTableDataSource<TableData>;
  public abstract readonly kpiCardData: IKpiCard | null;
  public abstract readonly deliverableType: EDeliverableType;

  public readonly sort = viewChild<MatSort>(MatSort);

  public readonly dotGraphData = signal<ILineGraphInputData[]>([]);
  public readonly keywordTableRowIds = signal<Record<string, string[]>>({});
  public readonly keywordTableRowIdsReady = signal<boolean>(false);
  public readonly selectedDataId = signal<string | null>(null);

  public readonly status = signal<EAnalyticsStatus>(EAnalyticsStatus.processing);
  public readonly streamUrl = signal<string>('');
  public readonly seekTime = signal<number | null>(null);
  public readonly dataCount = signal<number | null>(null);

  public readonly displayTimeFormat = DISPLAY_TIME_FORMAT;
  public readonly EMatchingType = EMatchingType;

  public readonly analyticsStatusBadgeLoading = computed(() => {
    return !this.findingsLoaded();
  });

  public readonly findingsLoaded = signal<boolean>(false);

  public readonly executionAnalyticsWithFindings = computed(() => {
    const analytics = this.executionAnalytics();
    const findings = this.findingsStore.findings$();

    if (!analytics || !this.findingsLoaded()) {
      return analytics;
    }

    return this.findingsService.mergeFindingsIntoAnalytics(
      analytics,
      findings,
      this.deliverableId()!,
      this.deliverableType,
    );
  });

  protected abstract readonly currentDeliverable: Signal<Deliverable | null | undefined>;
  protected readonly stream = computed(
    () => this.executionAnalyticsWithFindings()?.content?.socialContent as ITwitchStreamApi | IYoutubeStreamApi | null,
  );
  public readonly platformFeature = computed<EPlatformFeature | null>(() => {
    return this.executionAnalyticsWithFindings()?.platformFeature ?? this.executionAnalytics()?.platformFeature ?? null;
  });
  public readonly shouldRenderEmbeddedPlayer = computed(() => {
    return !!this.streamUrl() && !this.usesExternalVideoLink();
  });
  public readonly usesExternalVideoLink = computed(() => {
    return AnalyticsDetailsUtilitiesService.isExternalVideoLinkPlatformFeature(this.platformFeature());
  });

  public readonly manualFindingStatusStore = inject(ManualFindingStatusStore, { optional: true });

  public readonly getKeywordCount = (keyword: string): number | null => {
    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const keywords =
      (this.currentDeliverable() as unknown as IKeywordHolder<IFinding> | null | undefined)?.keywords ?? [];

    return AnalyticsDetailsUtilitiesService.computeKeywordCount(keyword, keywords, removedIds);
  };

  protected readonly translate = inject(TranslateService);
  protected readonly dateUtils = inject(DateUtilitiesService);
  protected readonly twitchStreamDetailsService = inject(StreamDetailsService);
  protected readonly graphUtilities = inject(GraphUtilitiesService);
  protected readonly durationUtils = inject(DurationUtilitiesService);
  protected readonly findingsStore = inject(DeliverableFindingsStore);
  protected readonly findingsService = inject(DeliverableFindingsService);
  private readonly findingDeleteService = inject(FindingDeleteService);
  private readonly eventTrackingService = inject(EventTrackingService);

  public readonly canDelete = computed(
    () => this.executionAnalytics()?.campaignProcessStatus === EProcessStatus.processing,
  );

  protected keywords: string[] = [];
  protected streamStartTime = '';
  private lastFindingsRequestKey: string | null = null;
  private readonly keywordSelectionIndices = new Map<string, number>();

  constructor() {
    effect(() => {
      if (!this.manualDeliverableStatusConfig().analyticsVisible) {
        this.findingsLoaded.set(true);
        this.keywordTableRowIdsReady.set(true);
        this.lastFindingsRequestKey = null;

        return;
      }

      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalytics();
      const socialContentId = executionAnalytics?.content?.socialContent?.uniqueExternalId;

      if (deliverableId && socialContentId) {
        const findingType = this.findingsService.mapDeliverableTypeToFindingType(this.deliverableType);

        if (findingType) {
          const findingsRequestKey = `${deliverableId}_${findingType}_${socialContentId}`;

          if (this.lastFindingsRequestKey === findingsRequestKey) {
            return;
          }

          this.lastFindingsRequestKey = findingsRequestKey;
          this.findingsLoaded.set(false);
          this.keywordTableRowIdsReady.set(false);
          this.findingsStore.fetchFindings({
            campaignExportId: this.manualDeliverableStatusConfig().exportMode
              ? this.manualDeliverableStatusConfig().campaignExportId
              : null,
            deliverableId,
            findingType,
            socialContentId,
          });
        }
      }
    });

    effect(() => {
      const loading = this.findingsStore.loading$();

      if (!loading) {
        this.findingsLoaded.set(true);
      }
    });
  }

  public highlightKeywords(text: string, rowKeywords?: string[]): string {
    return AnalyticsDetailsUtilitiesService.highlightKeywords(rowKeywords ?? this.keywords, text);
  }

  public onDataPointClick(clickedPointId: string): void {
    const clickedPoint = this.dataSource.data.find((data) => data.id === clickedPointId);

    if (!this.usesExternalVideoLink()) {
      this.twitchStreamDetailsService.seekToTimestamp(clickedPoint?.timestamp!, this.streamStartTime, this.seekTime);
    }

    this.selectDataPoint(clickedPointId);
    this.trackFindingEntryClicked();
  }

  public onRowClick(event: Event, element: TableData): void {
    event.stopPropagation();

    if (this.usesExternalVideoLink()) {
      this.openTimestampedVideoUrl(element.videoTimeSeconds ?? this.getStreamVideoTimeSeconds(element.timestamp));
      this.trackFindingEntryClicked();

      return;
    }

    this.twitchStreamDetailsService.seekToTimestamp(element.timestamp, this.streamStartTime, this.seekTime);
    this.trackFindingEntryClicked();
  }

  private trackFindingEntryClicked(): void {
    this.eventTrackingService.track(EEventType.campaignAnalyticsFindingEntryClicked, {
      contentStatus: this.status(),
      deliverableId: this.deliverableId() ?? undefined,
      deliverableType: this.deliverableType,
      matchingType: this.executionAnalytics()?.status,
      socialContentId: this.executionAnalytics()?.socialContentId ?? undefined,
    });
  }

  public buildDeleteMenuItems(
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
  ): Array<{ label: string; findingId: string; keyword: string; keywordId: string }> {
    return this.findingDeleteService.buildDeleteMenuItems(element, this.manualFindingStatusStore);
  }

  public onDeleteFinding(
    findingId: string,
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
  ): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    this.findingDeleteService.deleteFinding(findingId, element, this.manualFindingStatusStore, context);
  }

  public onDeleteAllFindings(element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    this.findingDeleteService.deleteAllFindings(element, this.manualFindingStatusStore, context);
  }

  public onRevertRow(element: Pick<IFindingVisualizer, 'id'>): void {
    this.findingDeleteService.revertRow(element, this.manualFindingStatusStore);
  }

  public hasRowPendingDeletions(element: Pick<IFindingVisualizer, 'id'>): boolean {
    return this.findingDeleteService.hasRowPendingDeletions(element, this.manualFindingStatusStore);
  }

  public hasRowActiveFinding(element: Pick<IFindingVisualizer, 'findings'>): boolean {
    return this.findingDeleteService.hasRowActiveFinding(element, this.manualFindingStatusStore);
  }

  public getActiveKeywords(element: Pick<IFindingVisualizer, 'findings' | 'keywordMappings'>): string[] {
    return this.findingDeleteService.getActiveKeywords(element, this.manualFindingStatusStore);
  }

  public isVisualizerActing(visualizerId: string): boolean {
    return this.findingDeleteService.isVisualizerActing(visualizerId, this.manualFindingStatusStore);
  }

  public isFindingInFlight(findingId: string): boolean {
    return this.findingDeleteService.isFindingInFlight(findingId, this.manualFindingStatusStore);
  }

  public onDeleteSingleRowFinding(element: { id: string; findingParentId: string }): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    const findingType = this.findingsService.mapDeliverableTypeToFindingType(this.deliverableType);

    if (!findingType) return;

    this.findingDeleteService.deleteSingleRowFinding(element, findingType, this.manualFindingStatusStore, context);
  }

  protected setStreamData(): void {
    if (this.platform() === EPlatform.twitch) {
      const twitchUrl = (this.stream() as ITwitchStreamApi)?.twitchMetaData.twitchUrl;

      if (twitchUrl) {
        this.streamUrl.set(twitchUrl);
      }
    }

    if (this.platform() === EPlatform.youtube) {
      const youtubeUrl = (this.stream() as IYoutubeStreamApi)?.youtubeLiveData?.youtubeUrl;

      if (youtubeUrl) {
        this.streamUrl.set(youtubeUrl);
      }
    }

    this.streamStartTime = this.executionAnalyticsWithFindings()?.content?.socialContent?.executionTime ?? '';
  }

  protected setStatus(): void {
    this.status.set(
      this.currentDeliverable()?.statusManual ?? this.currentDeliverable()?.status ?? EAnalyticsStatus.processing,
    );
  }

  protected setKeywordTableRowIds(keywordTableRowIds: Record<string, string[]>): void {
    this.keywordSelectionIndices.clear();
    this.keywordTableRowIds.set(keywordTableRowIds);
    this.keywordTableRowIdsReady.set(true);
  }

  protected getStreamVideoTimeSeconds(timestamp: string | null | undefined): number | null {
    return this.twitchStreamDetailsService.getSeekTimeInSeconds(timestamp, this.streamStartTime);
  }

  protected openTimestampedVideoUrl(videoTimeSeconds: number | null | undefined): void {
    const videoUrl = AnalyticsDetailsUtilitiesService.buildTimestampedVideoUrl(
      this.platformFeature(),
      videoTimeSeconds,
      this.streamUrl(),
    );

    if (!videoUrl) {
      return;
    }

    window.open(videoUrl, '_blank', 'noopener,noreferrer');

    this.eventTrackingService.track(EEventType.campaignAnalyticsExternalSourceOpened, {
      contentStatus: this.status(),
      matchingType: this.executionAnalytics()?.status,
      socialContentId: this.executionAnalytics()?.socialContentId ?? undefined,
    });
  }

  protected selectDataPoint(clickedPointId: string): void {
    this.selectedDataId.set(null);

    setTimeout(() => {
      this.selectedDataId.set(clickedPointId);
    });
  }

  public readonly jumpToNextKeywordFinding = (keyword: string): void => {
    const rowIds = this.keywordTableRowIds()[keyword] ?? [];

    if (!rowIds.length) {
      return;
    }

    const nextIndex = this.keywordSelectionIndices.get(keyword) ?? 0;
    const nextRowId = rowIds[nextIndex];

    this.keywordSelectionIndices.set(keyword, (nextIndex + 1) % rowIds.length);
    this.onDataPointClick(nextRowId);
  };

  protected abstract prepareChartAndTableData(): void;
  protected abstract setCounts(): void;
  protected abstract setKeywordList(): void;
}
